import { useCallback, useMemo } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  listErrorOf,
  type ConfigApi,
  type FilterCatalog,
  type InstanceApi,
  type ListErrorDetail,
} from '@eis/insuranceservice-sdk';
import type { BillingApi } from '@eis/billingservice-sdk';
import type { ClaimApi } from '@eis/claimservice-sdk';
import type { DataApi } from '@eis/dataservice-sdk';
import { CLAIM_FILTER_CATALOG, claimOrder } from './claims';
import { useApiClients } from './ApiClientsProvider';
import type { EisApiClients } from './clients';
import { apiKeys } from './keys';
import { isSet, listQueryKey } from './list-query';
import { isApiError, unwrap } from './result';

/**
 * `useInfiniteList` — every list of the console, one hook (SDD-017 §9, L-12).
 *
 * TanStack's `useInfiniteQuery` over the standard list contract: page 1 is requested without a
 * token, every later page with the previous page's `nextPageToken` (verbatim — a token is opaque)
 * and the CUT page 1 echoed (`validAt`/`asOf`, or `activeOn`/`asOf` for members). The token pins
 * the cut on the server; sending the echo too makes a drift a 400 instead of a silent new cut,
 * the rule `paginate()` follows. So a correction committed while the user scrolls neither
 * duplicates nor drops a row — and stays invisible until {@link InfiniteList.refresh}, which drops
 * every page and starts a new cut.
 *
 * Each collection's request is typed by the generated SDK (the switch in `fetchListPage`): a key
 * the service does not take is a type error here, not a silently ignored query parameter.
 */

/** The item each standard collection answers with — the generated SDK's own types. */
export interface ListCollections {
  readonly policies: InstanceApi.PolicyListEntry;
  readonly groupPolicies: InstanceApi.GroupPolicyListEntry;
  readonly groupPolicyMembers: InstanceApi.MemberProjection;
  readonly leads: InstanceApi.Lead;
  readonly tarifs: ConfigApi.Tarif;
  readonly groupTarifs: ConfigApi.GroupTarif;
  readonly invoices: BillingApi.Invoice;
  readonly policiesBillings: BillingApi.PolicyBilling;
  readonly policiesBillingDates: BillingApi.PolicyBilling;
  readonly billRuns: BillingApi.BillRun;
  /**
   * claimservice's claims (SDD-018 §9) — a FOREIGN list: its tokens are page numbers and its order
   * grammar is `field:asc|desc` (FACTS.md §4.3); both are translated here, so a screen pages and
   * sorts it like every other list (SDD-018 §13 DV-37).
   */
  readonly claims: ClaimApi.ClaimClass;
  /** The Data Suite's reports (SDD-024 L-1): another user's private report is never a row. */
  readonly reports: DataApi.Report;
  /** The Data Suite's custom datasets (SDD-024 L-1). */
  readonly customDatasets: DataApi.CustomDataset;
}

export type ListCollection = keyof ListCollections;

/** The collections that read one bitemporal cut (§6). */
export const TEMPORAL_COLLECTIONS: readonly ListCollection[] = [
  'policies',
  'groupPolicies',
  'groupPolicyMembers',
];

/** What a list asks for — the URL's state plus the knobs a screen sets. Never a token, never `asOf`. */
export interface InfiniteListQuery {
  readonly filters?: string;
  readonly search?: string;
  readonly order?: string;
  /** Temporal collections only (members: the `activeOn`). Absent = today, resolved by the server. */
  readonly validAt?: string;
  /**
   * "Covered at any time in a period" (SDD-021 PC-20) — `policies` and `groupPolicyMembers` only:
   * `coveredFrom` inclusive, `coveredTo` exclusive, both or neither. It REPLACES the business-day
   * cut (the service refuses both with VALID_AT_AND_PERIOD_EXCLUSIVE), so with a period set no
   * `validAt`/`activeOn` is sent.
   */
  readonly coveredFrom?: string;
  readonly coveredTo?: string;
  readonly expand?: string;
  readonly pageSize?: number;
  readonly includeTotal?: boolean;
}

/** The cut a temporal list was read at, as page 1 echoed it. */
export interface ListCut {
  readonly validAt?: string;
  readonly asOf?: string;
}

/** One page, as every standard list answers it. */
export interface ListPageOf<Item> {
  readonly items?: readonly Item[];
  readonly nextPageToken?: string;
  readonly totalSize?: number;
  readonly totalSizeCapped?: boolean;
  readonly validAt?: string;
  readonly asOf?: string;
  readonly activeOn?: string;
}

interface PageParam {
  readonly token: string;
  readonly cut: ListCut;
}

export interface InfiniteList<Item> {
  /** Every loaded row, in server order. */
  readonly items: readonly Item[];
  readonly pages: readonly ListPageOf<Item>[];
  /** Page 1 is on its way and nothing is shown yet (the skeleton state). */
  readonly isLoading: boolean;
  readonly isFetchingNextPage: boolean;
  /** Any request in flight — page 1, a next page or a refresh. */
  readonly isFetching: boolean;
  readonly hasNextPage: boolean;
  /** Loads the next page; a no-op while one is loading or at the end. */
  readonly fetchNextPage: () => void;
  /** The error of the last request, if it failed. */
  readonly error: unknown;
  /** The list refusal behind {@link error} (`INVALID_FILTER` with field and reason …), if it is one. */
  readonly listError: ListErrorDetail | undefined;
  /** Page 1 failed: nothing to show but the error. */
  readonly isError: boolean;
  /** A LATER page failed: the rows stay, the end of the grid offers a retry. */
  readonly isNextPageError: boolean;
  /** Retries what failed: page 1, or the next page. */
  readonly retry: () => void;
  /** Drops every page and starts again from page 1 — a new cut on a temporal list. */
  readonly refresh: () => void;
  /** Temporal lists: the cut page 1 read and every later page reads. */
  readonly cut: ListCut | undefined;
  /** With `includeTotal`: the count (a floor when {@link totalSizeCapped}). */
  readonly totalSize: number | undefined;
  readonly totalSizeCapped: boolean;
}

export interface UseInfiniteListOptions {
  /** The group policy whose members are listed (`groupPolicyMembers` only). */
  readonly code?: string;
  readonly enabled?: boolean;
}

/** The cache prefix of a collection: the one its writes already invalidate. */
function prefixOf(collection: ListCollection, code: string): readonly unknown[] {
  switch (collection) {
    case 'policies':
      return apiKeys.instance.policyList();
    case 'groupPolicies':
      return apiKeys.instance.groupPolicyList();
    case 'groupPolicyMembers':
      return apiKeys.instance.groupPolicyMembers(code);
    case 'leads':
      return apiKeys.instance.leadList();
    case 'tarifs':
      return apiKeys.config.tarifs();
    case 'groupTarifs':
      return apiKeys.config.groupTarifs();
    case 'invoices':
      return apiKeys.billing.invoiceList();
    case 'policiesBillings':
    case 'policiesBillingDates':
      return [...apiKeys.billing.scheduleList(), collection];
    case 'billRuns':
      return apiKeys.billing.runList();
    case 'claims':
      return apiKeys.claims.list();
    case 'reports':
      return apiKeys.reporting.reportList();
    case 'customDatasets':
      return apiKeys.reporting.customDatasetList();
  }
}

/** The standard parameters both shapes share; empty values are never sent. */
function standardQuery(query: InfiniteListQuery, pageToken: string | undefined) {
  return {
    ...(isSet(query.filters) ? { filters: query.filters } : {}),
    ...(isSet(query.search) ? { search: query.search } : {}),
    ...(isSet(query.order) ? { order: query.order } : {}),
    ...(isSet(query.expand) ? { expand: query.expand } : {}),
    ...(query.pageSize !== undefined && query.pageSize > 0 ? { pageSize: query.pageSize } : {}),
    ...(query.includeTotal === true ? { includeTotal: true } : {}),
    ...(isSet(pageToken) ? { pageToken } : {}),
  };
}

/** The period of a "covered during" read, when the query names a whole one (SDD-021 PC-20). */
function coveredOf(
  query: InfiniteListQuery,
): { readonly coveredFrom: string; readonly coveredTo: string } | undefined {
  return isSet(query.coveredFrom) && isSet(query.coveredTo)
    ? { coveredFrom: query.coveredFrom, coveredTo: query.coveredTo }
    : undefined;
}

/** The cut a later page sends: page 1's echo; page 1 sends only what the user chose. */
function cutQuery(query: InfiniteListQuery, param: PageParam | undefined): ListCut {
  if (param !== undefined) {
    // A period read has no business day; page 1's echo of one (there is none) must not creep in.
    return coveredOf(query) === undefined
      ? param.cut
      : { ...(isSet(param.cut.asOf) ? { asOf: param.cut.asOf } : {}) };
  }
  if (coveredOf(query) !== undefined) return {};
  return isSet(query.validAt) ? { validAt: query.validAt } : {};
}

/** One page of one collection, through its generated client. */
async function fetchListPage(
  clients: EisApiClients,
  collection: ListCollection,
  code: string,
  query: InfiniteListQuery,
  param: PageParam | undefined,
): Promise<ListPageOf<unknown>> {
  const base = standardQuery(query, param?.token);
  const cut = cutQuery(query, param);
  // Sent on every page, like the filters: the token pins the period, the echo makes a drift a 400.
  const covered = coveredOf(query) ?? {};
  const temporal = {
    ...(isSet(cut.validAt) ? { validAt: cut.validAt } : {}),
    ...(isSet(cut.asOf) ? { asOf: cut.asOf } : {}),
  };
  switch (collection) {
    case 'policies':
      return unwrap(
        clients.instance.client.GET('/v1/policies', {
          params: { query: { ...base, ...temporal, ...covered } },
        }),
      );
    case 'groupPolicies':
      return unwrap(
        clients.instance.client.GET('/v1/group-policies', {
          params: { query: { ...base, ...temporal } },
        }),
      );
    case 'groupPolicyMembers':
      return unwrap(
        clients.instance.client.GET('/v1/group-policies/{code}/members', {
          params: {
            path: { code },
            query: {
              ...base,
              // The members list calls its business date `activeOn` (SDD-017 D-7).
              ...(isSet(cut.validAt) ? { activeOn: cut.validAt } : {}),
              ...(isSet(cut.asOf) ? { asOf: cut.asOf } : {}),
              ...covered,
            },
          },
        }),
      );
    case 'leads':
      return unwrap(clients.instance.client.GET('/v1/leads', { params: { query: base } }));
    case 'tarifs':
      return unwrap(clients.config.client.GET('/v1/tarifs', { params: { query: base } }));
    case 'groupTarifs':
      return unwrap(clients.config.client.GET('/v1/group-tarifs', { params: { query: base } }));
    case 'invoices':
      return unwrap(
        clients.billing.client.GET('/billingservice/v1/invoices', { params: { query: base } }),
      );
    case 'policiesBillings':
      return unwrap(
        clients.billing.client.GET('/billingservice/v1/policies-billings', {
          params: { query: base },
        }),
      );
    case 'policiesBillingDates':
      return unwrap(
        clients.billing.client.GET('/billingservice/v1/invoices/policies-billing-dates', {
          params: { query: base },
        }),
      );
    case 'billRuns':
      return unwrap(
        clients.billing.client.GET('/billingservice/v1/bill-runs', { params: { query: base } }),
      );
    case 'claims': {
      const order = claimOrder(query.order);
      const page = await unwrap(
        clients.claims.GET('/claimservice/v1/claims', {
          params: {
            query: {
              ...(isSet(query.filters) ? { filters: query.filters } : {}),
              ...(isSet(query.search) ? { search: query.search } : {}),
              ...(order === undefined ? {} : { order }),
              ...(isSet(query.expand) ? { expand: query.expand } : {}),
              // claimservice caps a page at 50.
              pageSize: Math.min(query.pageSize ?? 50, 50),
              ...(isSet(param?.token) ? { pageToken: param.token } : {}),
            },
          },
        }),
      );
      return { items: page.items, nextPageToken: page.nextPageToken, totalSize: page.totalItems };
    }
    case 'reports':
      return unwrap(clients.data.client.GET('/dataservice/v1/reports', { params: { query: base } }));
    case 'customDatasets':
      return unwrap(
        clients.data.client.GET('/dataservice/v1/custom-datasets', { params: { query: base } }),
      );
  }
}

/** Page 1's echo, in the request's words (members echo `activeOn`). */
function cutOf(page: ListPageOf<unknown> | undefined): ListCut {
  const validAt = page?.validAt ?? page?.activeOn;
  return {
    ...(isSet(validAt) ? { validAt } : {}),
    ...(isSet(page?.asOf) ? { asOf: page.asOf } : {}),
  };
}

type RetryOption = boolean | number | ((failureCount: number, error: Error) => boolean) | undefined;

/**
 * A 4xx is the request's fault (an INVALID_FILTER stays one): it is never repeated. Anything else
 * follows the client's own retry policy — the app's default, or none in a story or a test.
 */
function retryPolicy(defaults: RetryOption): (failureCount: number, error: Error) => boolean {
  return (failureCount, error) => {
    if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
    if (typeof defaults === 'function') return defaults(failureCount, error);
    if (typeof defaults === 'number') return failureCount < defaults;
    return defaults ?? failureCount < 3;
  };
}

/** The cache key's view of the query: everything that changes the answer, never `asOf`. */
export function infiniteListKey(
  collection: ListCollection,
  query: InfiniteListQuery,
  code = '',
): readonly unknown[] {
  return apiKeys.lists.pages(
    prefixOf(collection, code),
    listQueryKey({
      filters: query.filters,
      search: query.search,
      order: query.order,
      validAt: query.validAt,
      coveredFrom: query.coveredFrom,
      coveredTo: query.coveredTo,
      expand: query.expand,
      pageSize: query.pageSize,
      includeTotal: query.includeTotal,
    }),
  );
}

export function useInfiniteList<C extends ListCollection>(
  collection: C,
  query: InfiniteListQuery = {},
  options: UseInfiniteListOptions = {},
): InfiniteList<ListCollections[C]> {
  const clients = useApiClients();
  const queryClient = useQueryClient();
  const code = options.code ?? '';
  const queryKey = infiniteListKey(collection, query, code);

  const result = useInfiniteQuery<
    ListPageOf<unknown>,
    Error,
    InfiniteData<ListPageOf<unknown>, PageParam | undefined>,
    readonly unknown[],
    PageParam | undefined
  >({
    queryKey,
    initialPageParam: undefined,
    queryFn: ({ pageParam }) => fetchListPage(clients, collection, code, query, pageParam),
    getNextPageParam: (last, pages) =>
      isSet(last.nextPageToken) ? { token: last.nextPageToken, cut: cutOf(pages[0]) } : undefined,
    enabled: (options.enabled ?? true) && (collection !== 'groupPolicyMembers' || code !== ''),
    retry: retryPolicy(queryClient.getDefaultOptions().queries?.retry),
  });

  const pages = useMemo(
    () => (result.data?.pages ?? []) as readonly ListPageOf<ListCollections[C]>[],
    [result.data],
  );
  const items = useMemo(() => pages.flatMap((page) => page.items ?? []), [pages]);
  const first = pages[0];

  const { fetchNextPage: fetchNext, hasNextPage, isFetchingNextPage, refetch } = result;
  const fetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNext();
  }, [fetchNext, hasNextPage, isFetchingNextPage]);

  const keyHash = JSON.stringify(queryKey);
  const refresh = useCallback(() => {
    void queryClient.resetQueries({ queryKey: JSON.parse(keyHash) as unknown[], exact: true });
  }, [queryClient, keyHash]);

  const hasData = result.data !== undefined;
  const retry = useCallback(() => {
    if (hasData) void fetchNext();
    else void refetch();
  }, [hasData, fetchNext, refetch]);

  const error: unknown = result.error;
  const listError = isApiError(error) ? listErrorOf(error.body) : undefined;
  const temporal = TEMPORAL_COLLECTIONS.includes(collection);

  return {
    items,
    pages,
    isLoading: result.isPending && result.fetchStatus !== 'idle',
    isFetchingNextPage,
    isFetching: result.isFetching,
    hasNextPage,
    fetchNextPage,
    error,
    listError,
    isError: result.isError && !hasData,
    isNextPageError: result.isFetchNextPageError,
    retry,
    refresh,
    cut: temporal && first !== undefined ? cutOf(first) : undefined,
    totalSize: first?.totalSize,
    totalSizeCapped: first?.totalSizeCapped === true,
  };
}

// ---------------------------------------------------------------------------------------------
// The filter catalog, value suggestions and the slice list
// ---------------------------------------------------------------------------------------------

export interface FilterCatalogOptions {
  /** Policies only: the tarif whose PRODUCT fields join the system ones. */
  readonly tarifSlug?: string;
  /** `groupPolicyMembers` only: the group policy. */
  readonly code?: string;
  readonly enabled?: boolean;
}

async function fetchCatalog(
  clients: EisApiClients,
  collection: ListCollection,
  options: FilterCatalogOptions,
): Promise<FilterCatalog> {
  const tarifSlug = options.tarifSlug;
  switch (collection) {
    case 'policies':
      return unwrap(
        clients.instance.client.GET('/v1/policies:filterCatalog', {
          params: { query: isSet(tarifSlug) ? { tarifSlug } : {} },
        }),
      );
    case 'groupPolicies':
      return unwrap(clients.instance.client.GET('/v1/group-policies:filterCatalog'));
    case 'groupPolicyMembers':
      return unwrap(
        clients.instance.client.GET('/v1/group-policies/{code}/members:filterCatalog', {
          params: { path: { code: options.code ?? '' } },
        }),
      );
    case 'leads':
      return unwrap(clients.instance.client.GET('/v1/leads:filterCatalog'));
    case 'tarifs':
      return unwrap(clients.config.client.GET('/v1/tarifs:filterCatalog'));
    case 'groupTarifs':
      return unwrap(clients.config.client.GET('/v1/group-tarifs:filterCatalog'));
    case 'invoices':
      return unwrap(clients.billing.client.GET('/billingservice/v1/invoices:filterCatalog'));
    case 'policiesBillings':
      return unwrap(
        clients.billing.client.GET('/billingservice/v1/policies-billings:filterCatalog'),
      );
    case 'policiesBillingDates':
      return unwrap(
        clients.billing.client.GET(
          '/billingservice/v1/invoices/policies-billing-dates:filterCatalog',
        ),
      );
    case 'billRuns':
      return unwrap(clients.billing.client.GET('/billingservice/v1/bill-runs:filterCatalog'));
    case 'claims':
      return CLAIM_FILTER_CATALOG;
    // The Data Suite's catalogs speak the same `common.list.v1` shape (SDD-017 §5.5).
    case 'reports':
      return (await unwrap(
        clients.data.client.GET('/dataservice/v1/reports:filterCatalog'),
      )) as unknown as FilterCatalog;
    case 'customDatasets':
      return (await unwrap(
        clients.data.client.GET('/dataservice/v1/custom-datasets:filterCatalog'),
      )) as unknown as FilterCatalog;
  }
}

/**
 * A collection's filter catalog (`GET …:filterCatalog`, §5.5) — what the property filter offers.
 * Cached for five minutes: it changes only when a tarif version is published.
 */
export function useFilterCatalog(
  collection: ListCollection,
  options: FilterCatalogOptions = {},
): UseQueryResult<FilterCatalog> {
  const clients = useApiClients();
  const scope = options.tarifSlug ?? '';
  return useQuery({
    queryKey: apiKeys.lists.catalog(prefixOf(collection, options.code ?? ''), scope),
    queryFn: () => fetchCatalog(clients, collection, options),
    staleTime: 5 * 60_000,
    enabled:
      (options.enabled ?? true) && (collection !== 'groupPolicyMembers' || isSet(options.code)),
  });
}

/**
 * The value suggestions of a policy catalog's `suggest` fields (`GET /v1/policies:filterValues`):
 * a loader the property filter calls with the field and what the user typed. Answers are cached
 * per (tarif, field, prefix), so typing back over a prefix asks nothing.
 */
export function usePolicyFilterValues(
  tarifSlug: string | undefined,
): (field: string, prefix: string) => Promise<readonly string[]> {
  const clients = useApiClients();
  const queryClient = useQueryClient();
  return useCallback(
    async (field: string, prefix: string) => {
      const scope = tarifSlug ?? '';
      const response = await queryClient.query({
        queryKey: apiKeys.lists.values(apiKeys.instance.policyList(), scope, field, prefix),
        queryFn: () =>
          unwrap(
            clients.instance.client.GET('/v1/policies:filterValues', {
              params: {
                query: { field, prefix, ...(isSet(tarifSlug) ? { tarifSlug } : {}) },
              },
            }),
          ),
        staleTime: 60_000,
      });
      return (response.values ?? []).flatMap((value) =>
        value.value === undefined ? [] : [value.value],
      );
    },
    [clients, queryClient, tarifSlug],
  );
}

/**
 * A policy's slices (`GET /v1/policies/{code}/snapshots`, §6): `validFrom`, `validTo`, the
 * operation that wrote each, ordered by `validFrom` — what the detail page's timeline strip shows.
 * Every page is read (a timeline is short); `asOf` reads the slices believed at that instant.
 */
export function usePolicySnapshots(
  code: string,
  asOf?: string,
  /** Also the beliefs a correction replaced, with their `recordedUntil` — the audit view. */
  includeSuperseded = false,
): UseQueryResult<InstanceApi.PolicySliceSummary[]> {
  const clients = useApiClients();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [
      ...apiKeys.instance.policySnapshots(code, asOf),
      includeSuperseded ? 'all' : 'current',
    ],
    queryFn: async () => {
      const slices: InstanceApi.PolicySliceSummary[] = [];
      let pageToken: string | undefined;
      let cut = asOf;
      for (;;) {
        const page: InstanceApi.ListPolicySnapshotsResponse = await unwrap(
          clients.instance.client.GET('/v1/policies/{code}/snapshots', {
            params: {
              path: { code },
              query: {
                ...(isSet(cut) ? { asOf: cut } : {}),
                ...(includeSuperseded ? { includeSuperseded: true } : {}),
                ...(isSet(pageToken) ? { pageToken } : {}),
                // The largest page: a slice says what changed against the slice before it ON THE
                // SAME PAGE, so a timeline read in one page names every change.
                pageSize: 200,
              },
            },
          }),
        );
        slices.push(...(page.items ?? []));
        if (!isSet(page.nextPageToken)) return slices;
        pageToken = page.nextPageToken;
        cut = page.asOf ?? cut;
      }
    },
    enabled: code !== '',
    // A refusal is an answer, not a fault: the superseded beliefs need the history scope, and a
    // caller without it is told so once (403) — retrying cannot change that.
    retry: retryPolicy(queryClient.getDefaultOptions().queries?.retry),
  });
}
